import React, { useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FilterList as FilterListIcon } from '@material-ui/icons';
import { Box, ListItem, ListItemText, Checkbox } from '@material-ui/core';

import MTSStyle from './MTSStyle';
import { deepClone } from 'helper/helper';
import dateIcon from 'assets/svgs/date.svg';
import pivotIcon from 'assets/svgs/pivot.svg';
import { PreviewElementContext } from '../Preview.context';
import { setSelectedList, setPivotOptions, setIsMTSFilterCollapsed } from '../Preview.actions';
import { MULTI_TIME_SELECTION, MTS_COLUMN_TITLE, PIVOT_MTS_VALUE } from 'constants/constant';

/**
 * MultiTimeSelectionField
 * Enable the user to select multiple time ranges in the filter
 * Locate under Time Period Column in Preview Page
 */
const MultiTimeSelectionField = ({ mtsSubAttr, parentName }) => {
	const classes = MTSStyle();
	const dispatch = useDispatch();
	const [isHovered, setIsHovered] = useState(false);
	const {
		selectedList,
		filterList: filterState,
		pivotOpts
	} = useSelector((state) => state.previewReducer);
	const { cbUsrSelection, cbSetShowPreviewElement } = useContext(PreviewElementContext);

	const mtsFilter = filterState?.find((filter) => filter?.localName === MULTI_TIME_SELECTION);
	const isMTSFilterSelected = mtsFilter && Object.keys(mtsFilter.mts ?? {}).length > 0;
	const isMTSColumnAdded = Boolean(
		selectedList?.find((filter) => filter?.localName === MULTI_TIME_SELECTION)
	);

	const otherFilterSelected = filterState?.some(
		(filter) =>
			filter?.field !== undefined &&
			filter?.localName !== MULTI_TIME_SELECTION &&
			['Walmart Calendar', 'Gregorian Calendar'].includes(filter?.parent)
	);
	const otherTimeColumnSelected = selectedList?.some(
		(column) =>
			column?.localName !== MULTI_TIME_SELECTION &&
			['Walmart Calendar', 'Gregorian Calendar'].includes(column?.parent)
	);

	const disabled = otherFilterSelected || otherTimeColumnSelected;
	// Show Pivot Icon on the element if it is selected/applied
	const pivotVisibility =
		isHovered || (pivotOpts?.pivotColumn === PIVOT_MTS_VALUE && isMTSFilterSelected)
			? 'visible'
			: 'hidden';

	const onFilterIconClick = (e) => {
		e.stopPropagation();
		// If MTS is selected as a column and its filter is set, then instead of setting the state to an empty MTS filter state,
		// we simply get the mtsFilter from redux and set it back in FilterForm Component
		if (isMTSFilterSelected && isMTSColumnAdded) {
			cbUsrSelection([{ ...mtsFilter, parent: parentName }]);
		} else {
			cbUsrSelection([{ ...mtsSubAttr, parent: parentName }]);
		}
		cbSetShowPreviewElement('columns', true, true);
		dispatch(setIsMTSFilterCollapsed(false));
	};

	const onFieldSelection = () => {
		// If MTS is checked and a value for its filter has been selected and saved, then
		// the user cannot un-check MTS - they'd have to delete MTS filte first.
		if (isMTSColumnAdded && isMTSFilterSelected) {
			return;
		}

		// Add column
		if (!isMTSColumnAdded) {
			dispatch(setSelectedList([...selectedList, deepClone(mtsSubAttr)]));
		} else {
			// Remove column
			const newSelectedList = selectedList.filter(
				(item) => item?.localName !== MULTI_TIME_SELECTION
			);
			dispatch(setSelectedList(newSelectedList));
		}
	};

	const onPivotClick = (e) => {
		e.stopPropagation();
		pivotOpts.showPopup = pivotOpts && pivotOpts.showPopup ? false : true;
		dispatch(setPivotOptions({ ...pivotOpts }));
	};

	return (
		<Box
			className={classes.MTSFieldContainer}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}>
			<ListItem onClick={onFieldSelection} className={classes.MTSField} disabled={disabled}>
				<Checkbox checked={isMTSColumnAdded} className={classes.checkBox} disabled={disabled} />
				<img src={dateIcon} alt="rb-date-icon" />
				<ListItemText className={classes.MTSText}>{MTS_COLUMN_TITLE}</ListItemText>
				{/* Pivot Icon */}
				<img
					style={{
						visibility: pivotVisibility
					}}
					src={pivotIcon}
					alt="rb-pivot-icon"
					onClick={(e) => onPivotClick(e)}
					className={classes.MTSPivotIcon}
				/>
				{/* Filter Icon */}
				<FilterListIcon
					fontSize="small"
					onClick={onFilterIconClick}
					className={classes.MTSFilterIcon}
					style={{
						visibility: isHovered || isMTSFilterSelected ? 'visible' : 'hidden'
					}}
				/>
			</ListItem>
		</Box>
	);
};

export default MultiTimeSelectionField;
